import {
  GSPLAT_CLIP_EPSILON,
  GSPLAT_CLIP_HEADER_TEXELS,
  GSPLAT_CLIP_HIGHLIGHT_COLOR,
  GSPLAT_MAX_CLIP_GROUPS,
  GSPLAT_MAX_CLIP_VOLUMES,
  GSplatClipVolumeKind,
} from '@/plugins/gsplat/clipping/GSplatClipState'
import { ClipPolygonMaxVerticeCount, ClipTask } from '@/shared/constants'

export default /* glsl */ `
// ============================================================
// GSplat polygon / Box 中心点裁切
// ============================================================

#ifdef GSPLAT_CLIP_ENABLED
uniform sampler2D gsplatClipData;
uniform int gsplatClipPolygonCount;
uniform vec3 gsplatClipWorldOffset;

const int GSPLAT_CLIP_MAX_VOLUMES = ${GSPLAT_MAX_CLIP_VOLUMES};
const int GSPLAT_CLIP_MAX_VERTICES = ${ClipPolygonMaxVerticeCount};
const int GSPLAT_CLIP_HEADER_TEXELS = ${GSPLAT_CLIP_HEADER_TEXELS};
const float GSPLAT_CLIP_EPSILON = ${GSPLAT_CLIP_EPSILON};
const int GSPLAT_CLIP_VOLUME_POLYGON = ${GSplatClipVolumeKind.POLYGON};
const int GSPLAT_CLIP_VOLUME_BOX = ${GSplatClipVolumeKind.BOX};
const int GSPLAT_CLIP_TASK_HIGHLIGHT = ${ClipTask.HIGHLIGHT};
const int GSPLAT_CLIP_TASK_SHOW_INSIDE = ${ClipTask.SHOW_INSIDE};
const int GSPLAT_CLIP_TASK_SHOW_OUTSIDE = ${ClipTask.SHOW_OUTSIDE};

vec4 fetchGSplatClipTexel(int volumeIndex, int texelIndex) {
  return texelFetch(gsplatClipData, ivec2(texelIndex, volumeIndex), 0);
}

mat4 getGSplatClipMatrix(int volumeIndex) {
  return mat4(
    fetchGSplatClipTexel(volumeIndex, 0),
    fetchGSplatClipTexel(volumeIndex, 1),
    fetchGSplatClipTexel(volumeIndex, 2),
    fetchGSplatClipTexel(volumeIndex, 3)
  );
}

bool gsplatClipPointOnSegment(vec2 point, vec2 start, vec2 end) {
  vec2 edge = end - start;
  vec2 offset = point - start;
  float edgeLengthSquared = dot(edge, edge);
  if (edgeLengthSquared <= GSPLAT_CLIP_EPSILON * GSPLAT_CLIP_EPSILON) {
    return dot(offset, offset) <= GSPLAT_CLIP_EPSILON * GSPLAT_CLIP_EPSILON;
  }

  float edgeLength = sqrt(edgeLengthSquared);
  float crossValue = edge.x * offset.y - edge.y * offset.x;
  if (abs(crossValue) > GSPLAT_CLIP_EPSILON * max(1.0, edgeLength)) {
    return false;
  }

  float dotValue = dot(offset, edge);
  return dotValue >= -GSPLAT_CLIP_EPSILON &&
    dotValue <= edgeLengthSquared + GSPLAT_CLIP_EPSILON;
}

bool gsplatPointInClipPolygon(vec3 center, int polygonIndex) {
  mat4 clipMatrix = getGSplatClipMatrix(polygonIndex);
  vec4 clipPosition = clipMatrix * vec4(center + gsplatClipWorldOffset, 1.0);
  if (clipPosition.w <= GSPLAT_CLIP_EPSILON) {
    return false;
  }

  vec2 point = clipPosition.xy / clipPosition.w;
  vec4 aabb = fetchGSplatClipTexel(polygonIndex, 4);
  if (
    point.x < aabb.x - GSPLAT_CLIP_EPSILON ||
    point.y < aabb.y - GSPLAT_CLIP_EPSILON ||
    point.x > aabb.z + GSPLAT_CLIP_EPSILON ||
    point.y > aabb.w + GSPLAT_CLIP_EPSILON
  ) {
    return false;
  }

  int vertexCount = int(round(fetchGSplatClipTexel(polygonIndex, 5).x));
  vec2 previous = fetchGSplatClipTexel(
    polygonIndex,
    GSPLAT_CLIP_HEADER_TEXELS + vertexCount - 1
  ).xy;
  bool inside = false;

  for (int vertexIndex = 0; vertexIndex < GSPLAT_CLIP_MAX_VERTICES; vertexIndex++) {
    if (vertexIndex >= vertexCount) {
      break;
    }

    vec2 current = fetchGSplatClipTexel(
      polygonIndex,
      GSPLAT_CLIP_HEADER_TEXELS + vertexIndex
    ).xy;
    if (gsplatClipPointOnSegment(point, previous, current)) {
      return true;
    }

    bool crossesY = (current.y > point.y) != (previous.y > point.y);
    if (crossesY) {
      float intersectionX =
        (previous.x - current.x) * (point.y - current.y) /
        (previous.y - current.y) + current.x;
      if (point.x < intersectionX) {
        inside = !inside;
      }
    }
    previous = current;
  }

  return inside;
}

bool gsplatPointInClipBox(vec3 center, int boxIndex) {
  vec4 boxPosition = getGSplatClipMatrix(boxIndex) *
    vec4(center + gsplatClipWorldOffset, 1.0);
  if (abs(boxPosition.w) <= GSPLAT_CLIP_EPSILON) {
    return false;
  }

  vec3 point = boxPosition.xyz / boxPosition.w;
  vec3 lower = vec3(-0.5 - GSPLAT_CLIP_EPSILON);
  vec3 upper = vec3(0.5 + GSPLAT_CLIP_EPSILON);
  return all(greaterThanEqual(point, lower)) && all(lessThanEqual(point, upper));
}

void evaluateGSplatClip(vec3 center, out bool hidden, out bool highlighted) {
  hidden = false;
  highlighted = false;
  int insideGroupMask = 0;
  int matchedInsideGroupMask = 0;

  for (int volumeIndex = 0; volumeIndex < GSPLAT_CLIP_MAX_VOLUMES; volumeIndex++) {
    if (volumeIndex >= gsplatClipPolygonCount) {
      break;
    }

    vec4 metadata = fetchGSplatClipTexel(volumeIndex, 5);
    int clipTask = int(round(metadata.y));
    int groupIndex = clamp(int(round(metadata.z)), 0, ${GSPLAT_MAX_CLIP_GROUPS - 1});
    int volumeKind = int(round(metadata.w));
    int groupBit = 1 << groupIndex;
    bool inside = volumeKind == GSPLAT_CLIP_VOLUME_BOX
      ? gsplatPointInClipBox(center, volumeIndex)
      : gsplatPointInClipPolygon(center, volumeIndex);

    if (clipTask == GSPLAT_CLIP_TASK_SHOW_OUTSIDE && inside) {
      hidden = true;
      highlighted = false;
      return;
    }

    if (clipTask == GSPLAT_CLIP_TASK_SHOW_INSIDE) {
      insideGroupMask |= groupBit;
      if (inside) {
        matchedInsideGroupMask |= groupBit;
      }
    } else if (clipTask == GSPLAT_CLIP_TASK_HIGHLIGHT && inside) {
      highlighted = true;
    }
  }

  hidden = (insideGroupMask & matchedInsideGroupMask) != insideGroupMask;
  if (hidden) {
    highlighted = false;
  }
}

void applyGSplatClipHighlight(bool highlighted, inout vec4 color) {
  #ifndef PICK_PASS
    if (highlighted) {
      color.rgb = vec3(
        ${GSPLAT_CLIP_HIGHLIGHT_COLOR.r},
        ${GSPLAT_CLIP_HIGHLIGHT_COLOR.g},
        ${GSPLAT_CLIP_HIGHLIGHT_COLOR.b}
      );
    }
  #endif
}
#else
void evaluateGSplatClip(vec3 center, out bool hidden, out bool highlighted) {
  hidden = false;
  highlighted = false;
}

void applyGSplatClipHighlight(bool highlighted, inout vec4 color) {
}
#endif
`
